// src/components/ChangePasswordPage.jsx
//
// "Change Password" destination for the header dropdown's Change
// Password link (an already-logged-in user, as opposed to
// ForgotPassword.jsx which is for a signed-out user who forgot theirs).
//
// Matches the "CHANGE PASSWORD" mockup: a plain Current Password field
// (verified server-side against the caller's own stored hash) and an
// Enter Password field with the same show/hide eye toggle + live
// checklist SetPassword.jsx/ForgotPassword.jsx already use, then a
// single SAVE button. This used to reuse the OTP-based /forgot-password
// + /reset-password flow (the only password-reset endpoint that
// existed at the time) -- there's now a dedicated PUT /change-password
// endpoint instead, so a logged-in user changing their own password no
// longer has to wait on an email OTP: the JWT itself is proof of who
// they are, so only the current password needs checking.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import PasswordChecklist, { PasswordInput, isPasswordValid } from "./PasswordField";
import { ROUTES } from "../config/routes";
import "../styles/Login.css";

const API_BASE = API_ORIGIN;

export default function ChangePasswordPage() {
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setMsg("");
    setSuccess("");

    if (!currentPassword) {
      setMsg("Enter your current password");
      return;
    }
    if (!isPasswordValid(newPassword)) {
      setMsg("Password does not meet all the requirements below");
      return;
    }

    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/change-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          CurrentPassword: currentPassword,
          NewPassword: newPassword,
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setMsg(data.message || "Unable to change password");
        return;
      }

      setSuccess("Your password has been changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setTimeout(() => navigate(ROUTES.DASHBOARD), 2000);
    } catch (err) {
      setMsg("Network error while changing your password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="d-flex align-items-center page-title-head">
        <div className="flex-grow-1">
          <h4 className="m-0 page-main-title">Change Password</h4>
        </div>
      </div>

      {/* Same white card + left-aligned column width as ProfilePage.jsx's
          "User Details" card, per request -- this page used to be a
          bare, centered form straight on the grey page background. */}
      <div className="row">
        <div className="col-12 col-md-8 col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom px-4 py-3">
              <h5 className="fw-bold text-green fs-5 m-0">Change Password</h5>
            </div>

            <form onSubmit={handleSave}>
              <div className="card-body p-4">
                {msg && (
                  <div className="alert alert-danger" role="alert">
                    {msg}
                  </div>
                )}
                {success && (
                  <div className="alert alert-success" role="alert">
                    {success}
                  </div>
                )}

                {/* Standard field width -- col-md-6, same as every field
                    on ProfilePage's card -- instead of stretching each
                    input the full width of this (wide) card. Each field
                    is its own row (stacked), not side-by-side. */}
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Current Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <div className="row g-3 mt-0">
                  <div className="col-md-6">
                    <label className="form-label">Enter Password:</label>
                    <PasswordInput
                      id="newPassword"
                      inputClassName="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder=""
                      autoComplete="new-password"
                    />
                    <PasswordChecklist password={newPassword} />
                  </div>
                </div>

                <button type="submit" className="btn btn-primary mt-3" disabled={saving}>
                  {saving ? "Saving..." : "SAVE"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
